import { to } from '@/helpers/range'

const MULTIPLIER = 1103515245
const INCREMENT = 12345
const MODULUS = Math.pow(2, 31)

/**
 * Linear Congruential Generator
 * @param {number} x Current value
 * @param {number} [a=1103515245] Multiplier
 * @param {number} [c=12345] Increment
 * @param {number} [m=2147483648] Modulus
 * @returns {number}
 */
function linearCongruentialGenerator (x, a = MULTIPLIER, c = INCREMENT, m = MODULUS) {
  return (x * a + c) % m
}

/**
 * Pseudo-Random number generator
 * @typedef {Object} PseudoRandomNumberGenerator
 */

/**
 * Creates a new random generator.
 * @param {number} [initialSeed = Date.now()] - Initial seed
 * @returns {PseudoRandomNumberGenerator}
 */
export function create (initialSeed = Date.now()) {
  let seed = initialSeed

  function value () {
    seed = linearCongruentialGenerator(seed)
    return seed / MODULUS
  }

  function reset (newSeed) {
    seed = newSeed
  }

  function floatBetween (min, max) {
    return to(value(), min, max)
  }

  function intBetween (min, max) {
    return Math.round(floatBetween(min, max))
  }

  function between (min, max, round = true) {
    return round === true
      ? intBetween(min, max)
      : floatBetween(min, max)
  }

  function normal (min, max) {
    return (intBetween(min, max) + intBetween(min, max) + intBetween(min, max)) / 3
  }

  function betweenRange ([min, max], round = true) {
    return between(min, max, round)
  }

  function index (list) {
    if (list.length === 0) {
      return -1
    }
    return intBetween(0, list.length - 1)
  }

  function item (list) {
    if (list.length === 0) {
      return undefined
    }
    return list[index(list)]
  }

  return {
    reset,
    value,
    floatBetween,
    intBetween,
    between,
    betweenRange,
    index,
    item,
    normal
  }
}

export default {
  create
}
